import React, { useState } from 'react';
import { Form, Button, Typography, message } from 'antd';
import { UserOutlined, LockOutlined, ArrowRightOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ForgotPassword from './ForgotPassword';
import './Login.css';

const { Text } = Typography;

const fieldBox = {
  display: 'flex',
  alignItems: 'center',
  background: 'rgba(255,255,255,0.10)',
  border: '1px solid rgba(255,255,255,0.22)',
  borderRadius: '10px',
  height: '52px',
  padding: '0 14px',
  gap: '10px',
};

const nativeInput = {
  flex: 1,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  color: 'white',
  fontSize: '15px',
  lineHeight: '52px',
};

const iconStyle = { color: 'rgba(255,255,255,0.45)', fontSize: '16px', flexShrink: 0 };

/* Custom text field — integrates with Ant Design Form */
const CustomInput = React.forwardRef(({ value, onChange, placeholder }, ref) => (
  <div style={fieldBox}>
    <UserOutlined style={iconStyle} />
    <input
      ref={ref}
      type="text"
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
      style={nativeInput}
      autoComplete="username"
      className="login-native-input"
    />
  </div>
));
CustomInput.displayName = 'CustomInput';

/* Custom password field with show/hide toggle */
const CustomPassword = React.forwardRef(({ value, onChange, placeholder }, ref) => {
  const [visible, setVisible] = useState(false);
  return (
    <div style={fieldBox}>
      <LockOutlined style={iconStyle} />
      <input
        ref={ref}
        type={visible ? 'text' : 'password'}
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        style={nativeInput}
        autoComplete="current-password"
        className="login-native-input"
      />
      <span
        style={{ ...iconStyle, cursor: 'pointer' }}
        onClick={() => setVisible(v => !v)}
      >
        {visible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
      </span>
    </div>
  );
});
CustomPassword.displayName = 'CustomPassword';

function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const user = await login(values.email, values.password);
      message.success('Login effettuato con successo!');
      if (user?.must_change_password) {
        navigate('/change-password');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      message.error(error.response?.data?.detail || 'Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  if (showForgotPassword) {
    return <ForgotPassword onBack={() => setShowForgotPassword(false)} />;
  }

  return (
    <div className="login-page">
      {/* Animated background blobs */}
      <div className="login-bg">
        <div className="blob blob-1" />
        <div className="blob blob-2" />
        <div className="blob blob-3" />
      </div>

      {/* Left panel — branding */}
      <div className="login-left">
        <div className="login-brand">
          <img src="/logo_nke_login.png" alt="NKE Sterling" className="login-logo-nke" />
          <img src="/logo_app.png?v=2" alt="Arkivio QHSE" className="login-logo-app" />
          <p className="login-tagline">
            Gestione documentale HSE<br />
            <span>Conformità · Sicurezza · Ambiente</span>
          </p>
        </div>
        <div className="login-features">
          <div className="feature-item"><span className="feature-dot" />Tracciamento documenti di conformità</div>
          <div className="feature-item"><span className="feature-dot" />Monitoraggio scadenze in tempo reale</div>
          <div className="feature-item"><span className="feature-dot" />Gestione multi-tenant sicura</div>
          <div className="feature-item"><span className="feature-dot" />Notifiche automatiche via email</div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="login-right">
        <div className="login-card">
          <div className="login-card-header">
            <div className="login-icon-box">
              <LockOutlined />
            </div>
            <h2>Accedi alla piattaforma</h2>
            <Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: 14 }}>
              Inserisci le tue credenziali per continuare
            </Text>
          </div>

          <Form
            name="login"
            onFinish={onFinish}
            autoComplete="off"
            layout="vertical"
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Inserisci la tua email!' },
                { type: 'email', message: 'Email non valida!' }
              ]}
            >
              <CustomInput placeholder="Indirizzo email" />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Inserisci la password!' }]}
            >
              <CustomPassword placeholder="Password" />
            </Form.Item>

            <Form.Item style={{ marginTop: 8, marginBottom: 12 }}>
              <Button
                type="primary"
                htmlType="submit"
                block
                size="large"
                loading={loading}
                icon={<ArrowRightOutlined />}
                iconPosition="end"
                style={{
                  height: '52px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #1565c0, #1e88e5)',
                  border: 'none',
                  fontSize: '15px',
                  fontWeight: 600,
                  boxShadow: '0 4px 20px rgba(21,101,192,0.45)',
                }}
              >
                Accedi
              </Button>
            </Form.Item>

            <div style={{ textAlign: 'center' }}>
              <Button
                type="link"
                onClick={() => setShowForgotPassword(true)}
                style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13 }}
              >
                Password dimenticata?
              </Button>
            </div>
          </Form>

          <div className="login-footer">
            <Text style={{ color: 'rgba(255,255,255,0.25)', fontSize: 12 }}>
              © {new Date().getFullYear()} NKE Sterling - Arkivio
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
